<script setup lang="ts">
// Import Swiper styles
import 'swiper/swiper-bundle.min.css';

import { uniqueId } from 'lodash-es';
// import Swiper core and required modules
import SwiperCore, { Navigation } from 'swiper';
// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import ICON_SIGNAL from '@/assets/icons/signal.svg';
import { SKU_COLLAPSE_STYLE, SKU_STYLE_TYPE } from '@/store/constants';
import IconFa6SolidImage from '~icons/fa6-solid/image';

// FIXME: 改用 inline 引入
import ICON_SKU_ARROW from '../../assets/icons/sku-arrow.svg';
import ICON_SKU_ARROW_DISABLED from '../../assets/icons/sku-arrow-disabled.svg';

// install Swiper modules
SwiperCore.use([Navigation]);

interface NormalizedSku {
  id: string | number;
  isSelected?: boolean;
  isSoldOut?: boolean;
  image?: string;
  [key: string]: any;
}

interface ProductData {
  isOnSell: boolean;
  isSoldOut: boolean;
  isBeforeSell?: boolean;
  isEndSell?: boolean;
  normalizedSkus: NormalizedSku[];
  skuName?: string;
  originPriceDisplay?: string;
  priceDisplay?: string;
  buttonName?: string;
  [key: string]: any;
}

interface Props {
  product?: ProductData | null;
  isShowOriginalPrice?: boolean;
  isShowPrice?: boolean;
  isShowShoppingCart?: boolean;
  productNameStyle?: Record<string, any>;
  productPriceStyle?: Record<string, any>;
  productButtonStyle?: Record<string, any>;
  skuType?: string;
  skuImageStyle?: string;
  originalPriceDescription?: string;
  /** Panasonic 客製：價格上方顯示「オンラインストア価格」文字
   *  https://17media.atlassian.net/browse/HU-9825 */
  isShowPanasonicOnlineStoreLabel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  product: null,
  isShowOriginalPrice: false,
  isShowPrice: false,
  isShowShoppingCart: false,
  productNameStyle: () => ({}),
  productPriceStyle: () => ({}),
  productButtonStyle: () => ({}),
  skuType: SKU_STYLE_TYPE.EXPAND,
  skuImageStyle: SKU_COLLAPSE_STYLE.SQUARE,
  originalPriceDescription: '',
  isShowPanasonicOnlineStoreLabel: false,
});

const emit = defineEmits(['skuClicked', 'shoppingButtonClicked']);

const { t } = useI18n();

const slidesPerView = 3;
const swiperId = uniqueId();
const swiperOption: any = {
  slidesPerView,
  spaceBetween: 2,
  watchOverflow: true,
  resizeObserver: true,
  allowTouchMove: false,
  navigation: {
    prevEl: `.swiper-button-prev_${swiperId}`,
    nextEl: `.swiper-button-next_${swiperId}`,
  },
};

const clickSku = (product: any, sku: any) => {
  emit('skuClicked', { product, sku });
};

const canSell = computed(() => {
  return props.product?.isOnSell && !props.product?.isSoldOut;
});

const unavailableBadgeTextDisplay = computed(() => {
  if (!props.product) { return ''; }

  if (props.product.isBeforeSell) { return t('cms_setting.display.product_before_sell_badge'); }
  if (props.product.isEndSell) { return t('cms_setting.display.product_end_sell_badge'); }
  if (props.product.isSoldOut) { return t('cms_setting.display.product_sold_out_badge'); }

  return '';
});

const clickShoppingCart = (product: any) => {
  // FIXME：
  // 待確認為何不是使用 props.product ？
  if (!canSell.value) { return; }
  emit('shoppingButtonClicked', product);
};

const skuCarouselStyleClass = computed(() => {
  if (props.skuType === SKU_STYLE_TYPE.EXPAND) { return null; }
  if (!props.product?.normalizedSkus) { return []; }

  return props.product.normalizedSkus.map((sku) => ({
    'product-sku-carousel__item--selected': sku.isSelected,
    'product-sku-carousel__item--sold-out': sku.isSoldOut,
  }));
});

const cursorStyle = computed(() => {
  return !canSell.value
    ? 'cursor-not-allowed'
    : 'cursor-pointer';
});

const shouldShowSkuCarousel = computed(() => {
  if (props.skuType === SKU_STYLE_TYPE.EXPAND) { return false; }
  if (!props.product?.normalizedSkus) { return false; }

  const skus = props.product.normalizedSkus;
  return [
    props.skuType === SKU_STYLE_TYPE.COLLAPSE,
    props.skuImageStyle !== SKU_COLLAPSE_STYLE.HIDDEN,
    (skus.length === 1 && skus[0].imageUrl) || (skus.length > 1),
  ].every(Boolean);
});

const shouldShowSkuName = computed(() => {
  if (props.skuType === SKU_STYLE_TYPE.EXPAND) { return true; }
  if (!props.product?.normalizedSkus) { return false; }

  const skus = props.product.normalizedSkus;
  return props.skuImageStyle !== SKU_COLLAPSE_STYLE.HIDDEN || skus.length === 1;
});

// NOTE: This will never show with shouldShowSkuCarousel and shouldShowSkuName
const shouldShowSkuDescription = computed(() => {
  if (props.skuType === SKU_STYLE_TYPE.EXPAND) { return false; }
  if (!props.product?.normalizedSkus) { return false; }

  const skus = props.product.normalizedSkus;
  return [
    props.skuType === SKU_STYLE_TYPE.COLLAPSE,
    props.skuImageStyle === SKU_COLLAPSE_STYLE.HIDDEN,
    skus.length > 1,
  ].every(Boolean);
});
</script>

<template>
<div class="product">
  <!---
    NOTE: 攤平結構為了讓之後改成 grid 可能會好改一點
  -->
  <div class="image-box" @click="clickShoppingCart(product)" :class="cursorStyle">
    <template v-if="product?.imageUrl">
      <div class="image">
        <el-image class="cover w-full h-full" :src="product.imageUrl" fit="cover"/>
      </div>
    </template>
    <template v-else>
      <div class="placeholder">
        <IconFa6SolidImage class="placeholder__image text-gray-700"/>
      </div>
    </template>

    <div class="live" v-if="product?.isPromoting">
      <el-image class="signal-icon" :src="ICON_SIGNAL"/>
      <div class="text">{{ $t("cms_setting.display.product_live_badge") }}</div>
    </div>

    <div class="unavailable-badge" v-show="!canSell">
      <span>{{ unavailableBadgeTextDisplay }}</span>
    </div>

    <div class="hover-overlay" v-show="canSell"></div>
  </div>

  <div
    class="product-name mb-3 line-clamp-3"
    :style="productNameStyle"
    :class="cursorStyle"
    @click="clickShoppingCart(product)"
  >{{ product?.name }}</div>

  <div class="product-sku-container">
    <div class="product-sku-carousel cursor-auto" v-if="shouldShowSkuCarousel">
      <Swiper class="swiper w-auto" v-bind="swiperOption">
        <SwiperSlide
          v-for="(sku, skuIndex) in product?.normalizedSkus"
          :key="sku.id"
          :swiperRef="null as any"
        >
          <div
            class="product-sku-carousel__item"
            :class="[skuImageStyle, skuCarouselStyleClass?.[skuIndex]]"
            @click="clickSku(product, sku)"
          >
            <template v-if="sku.imageUrl">
              <el-image
                class="product-sku-carousel__item__image"
                :src="sku.imageUrl"
                fit="cover"
              />
            </template>
            <template v-else>
              <div class="product-sku-carousel__item__placeholder">
                <IconFa6SolidImage class="text-base text-gray-700"/>
              </div>
            </template>
          </div>
        </SwiperSlide>
      </Swiper>

      <template v-if="(product?.normalizedSkus?.length || 0) > slidesPerView">
        <div class="product-sku-carousel__left" :class="`swiper-button-prev_${swiperId}`">
          <img class="product-sku-carousel__left--enable" :src="ICON_SKU_ARROW"/>
          <img
            class="product-sku-carousel__left--disable"
            :src="ICON_SKU_ARROW_DISABLED"
          />
        </div>
        <div class="product-sku-carousel__right" :class="`swiper-button-next_${swiperId}`">
          <img class="product-sku-carousel__left--enable" :src="ICON_SKU_ARROW"/>
          <img
            class="product-sku-carousel__left--disable"
            :src="ICON_SKU_ARROW_DISABLED"
          />
        </div>
      </template>
    </div>
  </div>

  <div class="fill-empty"></div>

  <div
    class="product-sku-name"
    v-if="shouldShowSkuName"
    :class="cursorStyle"
    @click="clickShoppingCart(product)"
  >{{ product?.skuName }}</div>

  <div
    class="product-sku-description"
    v-if="shouldShowSkuDescription"
    :class="cursorStyle"
    @click="clickShoppingCart(product)"
  >{{ t("cms_setting.display.product.sku_description") }}</div>

  <!---
    原價
  -->
  <div
    class="product-original-price-container"
    :class="cursorStyle"
    @click="clickShoppingCart(product)"
  >
    <template v-if="isShowPrice && isShowOriginalPrice">
      <div class="product-original-price-description">{{ product?.originPriceDisplay ? originalPriceDescription : "" }}</div>
      <!---
        FIXME: 處理價格過長問題
      -->
      <div class="product-original-price line-through">{{ product?.originPriceDisplay }}</div>
    </template>
  </div>

  <!-- Panasonic 客製：價格上方顯示「オンラインストア価格」文字
       https://17media.atlassian.net/browse/HU-9825 -->
  <div
    v-if="isShowPrice && isShowPanasonicOnlineStoreLabel"
    class="panasonic-online-store-label"
  >
    {{ t('cms_setting.display.product_panasonic_online_store_label') }}
  </div>

  <div
    class="product-price-container"
    :class="cursorStyle"
    @click="clickShoppingCart(product)"
  >
    <template v-if="isShowPrice">
      <div class="product-price mb-2 font-bold" :style="productPriceStyle">{{ product?.priceDisplay }}</div>
    </template>
  </div>

  <!---
    CTA 按鈕
  -->
  <button
    class="button"
    v-if="isShowShoppingCart"
    type="button"
    :disabled="!canSell"
    :style="productButtonStyle"
    @click="clickShoppingCart(product)"
  >{{ product?.buttonName }}</button>
</div>
</template>
<style lang="scss" scoped>
$live-badge-bg: #FF0000;
$slides-per-view: 3;
$sku-gap: 6px;
$sku-size: 36px;
$sku-image-size: 30px;
$sku-width: $sku-size * $slides-per-view + $sku-gap * ($slides-per-view - 1);

.product {
  @apply flex flex-col;
  @apply w-full relative mb-6 p-3;

  transition: box-shadow 0.3s;

  .product-original-price-container {
    @apply text-xs text-gray-800;

    .product-original-price-description,
    .product-original-price {
      @include keep-text-line-height;
    }
  }

  .panasonic-online-store-label {
    @apply text-xs mb-1;
  }

  &:hover {
    @apply rounded;

    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
  // NOTE: 只有桌機版需要有效果，再看看要不要抽成 mixin
  @media (pointer: fine) {
    &:hover {
      @apply rounded;

      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }
  }

  .fill-empty {
    flex: 1 1 100%;
  }

  .product-name,
  .product-sku-name,
  .product-sku-description {
    flex: 0 0 auto;
  }

  .product-sku-name,
  .product-sku-description {
    @apply text-xs text-black font-normal mb-2;

    @include keep-text-line-height;
  }

  .image-box {
    @apply relative flex justify-center items-center mx-auto bg-gray-600 mb-2;

    @include aspect-ratio($height: 1, $width: 1);

    .placeholder {
      @apply flex flex-col items-center justify-center;
    }

    .image,
    .placeholder {
      @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform;
      @apply w-full h-full;
    }

    .placeholder__image {
      @apply text-4xl;
    }

    .live {
      @apply absolute left-0 top-0;
      @apply h-5 pl-px pr-1.5 rounded-br-lg;
      @apply flex items-center justify-center;

      background-color: $live-badge-bg;

      .signal-icon {
        @apply w-3 h-3 flex-shrink-0 flex-grow-0;
      }

      .text {
        @apply text-xs font-bold text-white flex-shrink-0 flex-grow-0;
      }
    }

    .unavailable-badge {
      @apply bg-black bg-opacity-70 text-white;
      @apply inline-block py-1 px-2 whitespace-nowrap;
      @apply absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2;
    }

    .hover-overlay {
      @apply bg-white absolute inset-0 bg-opacity-50 hidden;
    }

    &:hover {
      .hover-overlay {
        @apply block;
      }
    }
  }

  &-sku-carousel {
    @apply relative mx-auto mb-2;

    max-width: $sku-width;

    &__item {
      @apply flex justify-center items-center;
      @apply w-9 h-9 rounded-sm overflow-hidden;
      @apply border border-transparent;
      @apply select-none;

      -webkit-tap-highlight-color: transparent;

      &__image {
        @apply rounded-sm;

        width: $sku-image-size;
        height: $sku-image-size;
      }

      &__placeholder {
        @apply flex items-center justify-center;
        @apply bg-gray-300;
        @apply rounded-sm;

        width: $sku-image-size;
        height: $sku-image-size;
      }

      // NOTE: The following 2 class will be applied from skuImageStyle
      &.circle {
        @apply rounded-full;
      }

      &.circle &__image,
      &.circle &__placeholder {
        @apply rounded-full;
      }

      &.hidden {
        display: none;
      }

      &--selected,
      &--selected.placeholder, {
        @apply border-black cursor-pointer;
      }

      @media (pointer: fine) {
        &:hover {
          @apply border-black cursor-pointer;
        }
      }

      &--sold-out {
        @apply relative;

        &::after {
          @apply h-px bg-white;
          @apply absolute -left-2 -right-2 top-1/2 -translate-y-1/2 rotate-45 transform;

          // NOTE: 點擊到 pseudo-element 的時候會吃不到點擊事件
          // REF: https://17media.atlassian.net/browse/HU-3431
          @apply pointer-events-none;

          content: '';
        }
      }
    }

    :deep(.swiper-container) {
      @apply text-center h-auto;
    }

    :deep(.swiper-wrapper) {
      @apply inline-flex p-0;

      width: auto !important;
    }

    &__left,
    &__right {
      @apply absolute top-1/2 -translate-y-1/2 transform;
      @apply w-6 h-6 flex items-center justify-center;
      @apply select-none;

      &:hover {
        @apply cursor-pointer;
      }

      &--disable {
        @apply hidden;
      }

      &.swiper-button-disabled {
        .product-sku-carousel__left,
        .product-sku-carousel__right {
          &--enable {
            @apply hidden;
          }

          &--disable {
            @apply block;
          }
        }
      }
    }

    &__left {
      @apply -left-6;
    }

    &__right {
      @apply -right-6;

      img {
        transform: scaleX(-1);
      }
    }
  }

  .button {
    @apply w-full;
    @apply border border-black border-solid rounded-sm outline-none;
    @apply transition ease-linear;

    min-height: 32px; /* min-h-8 */
    color: var(--custom_product_button_color, black);
    background-color: var(--custom_product_button_bg_color, white);
    opacity: var(--custom_product_button_opacity, 1);
    border-color: var(--custom_product_button_border_color, black);

    &:disabled {
      @apply cursor-not-allowed bg-transparent;
      @apply border-gray-800 text-gray-800;
    }
  }

  .button:enabled {
    @include rwd-hover {
      color: var(--custom_product_button_color_hover, white);
      background-color: var(--custom_product_button_bg_color_hover, black);
      opacity: var(--custom_product_button_opacity_hover, 1);
    }
  }
}

.tablet .product {
  .image-box {
    .placeholder__image {
      @apply text-6xl;
    }

    .live {
      @apply h-6 pl-1 pr-2;

      .signal-icon {
        @apply w-4 h-4;
      }

      .text {
        @apply text-sm;
      }
    }
  }
}

.laptop .product {
  .image-box {
    .placeholder__image {
      @apply text-6xl;
    }

    .live {
      @apply h-6 pl-1 pr-2;

      .signal-icon {
        @apply w-4 h-4;
      }

      .text {
        @apply text-sm;
      }
    }
  }

  .button {
    min-height: 36px; /* min-h-9 */
  }
}

.desktop .product {
  .image-box {
    .placeholder__image {
      @apply text-6xl;
    }

    .live {
      @apply h-6 pl-1 pr-2;

      .signal-icon {
        @apply w-4 h-4;
      }

      .text {
        @apply text-sm;
      }
    }
  }

  .button {
    min-height: 36px; /* min-h-9 */
  }
}
</style>
